/**
 * GeneralConfig Component
 *
 * Configuration component for general task information.
 * Allows editing the task name and displays metadata like creation/update dates.
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 */

import React from "react";
import { HiPencil, HiClock, HiGlobeAlt } from "react-icons/hi2";

const GeneralConfig = ({ config, onChange }) => {
  const handleNameChange = (e) => {
    onChange({ ...config, name: e.target.value });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-2.5">
      {/* Task Name */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-indigo-100 dark:bg-indigo-500/15 mr-2">
            <HiPencil className="text-indigo-500 dark:text-indigo-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Name</h3>
        </div>

        <div className="space-y-2">
          <input
            type="text"
            value={config.name || ""}
            onChange={handleNameChange}
            placeholder="Enter task name..."
            className="w-full px-3 py-2 text-sm rounded-lg
                       bg-gray-50 dark:bg-gray-800/50
                       border border-gray-200 dark:border-gray-700/50
                       text-gray-900 dark:text-white
                       placeholder-gray-400 dark:placeholder-gray-500
                       focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500
                       transition-all"
          />
          <p className="text-2xs text-gray-400 dark:text-gray-500">
            Give your automation a descriptive name
          </p>
        </div>
      </div>

      {/* Metadata (Read-only) */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700/30 mr-2">
            <HiClock className="text-gray-500 dark:text-gray-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Metadata</h3>
        </div>

        <div className="space-y-3">
          {/* Website URL */}
          {config.websiteUrl && (
            <div className="flex items-start gap-3 py-1.5 px-2 rounded-lg bg-gray-50 dark:bg-gray-800/30">
              <HiGlobeAlt className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-0.5">Website</label>
                <p className="text-sm text-gray-700 dark:text-gray-300 truncate" title={config.websiteUrl}>
                  {config.websiteUrl}
                </p>
              </div>
            </div>
          )}

          {/* Created Date */}
          <div className="flex items-center justify-between py-1.5 px-2">
            <label className="text-sm text-gray-500 dark:text-gray-400">Created</label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {formatDate(config.createdAt)}
            </span>
          </div>

          {/* Updated Date */}
          <div className="flex items-center justify-between py-1.5 px-2">
            <label className="text-sm text-gray-500 dark:text-gray-400">Last Updated</label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {formatDate(config.updatedAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralConfig;
